import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../../store';
import { getUserType, isBetaEnv } from '../../../utils/authUtils';
import { getUserManual } from '../../../store/actions/DataAction';
import Badge from '../../shared/TailwindComponents/Badge';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import GlassCard from '../../shared/brand/GlassCard';
import { sanitizeHTML } from '../../../utils/security/htmlSanitizer';
import {
  PiRocketLight,
  PiLightbulbLight,
  PiLockLight,
  PiWrenchLight,
  PiGearLight,
  PiListLight,
  PiTagLight,
  PiGridFourLight,
  PiFolderLight,
  PiChatsLight,
  PiQuestionLight,
} from 'react-icons/pi';
import { Section, ContentItem } from './types';

const iconMap: { [key: string]: React.ReactNode } = {
  'Get Started': <PiRocketLight className="h-4 w-4 mr-1" />,
  'General Info & Tips': <PiLightbulbLight className="h-4 w-4 mr-1" />,
  'Vault': <PiLockLight className="h-4 w-4 mr-1" />,
  'Tools': <PiWrenchLight className="h-4 w-4 mr-1" />,
  'Operations': <PiGearLight className="h-4 w-4 mr-1" />,
  'Utility Bar': <PiListLight className="h-4 w-4 mr-1" />,
  'Pricing Information': <PiTagLight className="h-4 w-4 mr-1" />,
  'Dashboard': <PiGridFourLight className="h-4 w-4 mr-1" />,
  'Documents': <PiFolderLight className="h-4 w-4 mr-1" />,
  'Messages': <PiChatsLight className="h-4 w-4 mr-1" />,
};

const Help: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userType = getUserType();
  const helpDataState = useSelector((state: RootState) => state.lists);

  useEffect(() => {
    if (userType) {
      dispatch(getUserManual(userType, 'en'));
    }
  }, [dispatch, userType]);

  const scrollToElement = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (helpDataState.loading) {
    // Fetching a manual is not an AI process, so it gets the content-shaped
    // skeleton rather than the brand loader.
    return <Skeleton variant="detail" />;
  }

  if (helpDataState.error || !helpDataState.data) {
    return (
      <div className="flex items-center justify-center w-full h-full text-center font-sans text-sm">
        {t('Help content is coming soon.')}
      </div>
    );
  }

  if (isBetaEnv) {
    return (
      <div className="flex items-center justify-center w-full h-64 text-center font-sans text-sm">
        <Badge color="gray">{t('Help content is coming soon')}</Badge>
      </div>
    );
  }

  const currentHelpData = helpDataState.data;

  return (
    <div className="flex">
      <LeftSidePanel sections={currentHelpData.sections} scrollToElement={scrollToElement} />
      <RightContent sections={currentHelpData.sections} message={currentHelpData.message} />
    </div>
  );
};

interface LeftSidePanelProps {
  sections: Section[];
  scrollToElement: (id: string) => void;
}

const LeftSidePanel: React.FC<LeftSidePanelProps> = ({ sections, scrollToElement }) => {
  const { t } = useTranslation();
  return (
    <div className="w-64 bg-bg border-r border-hairline p-3 sticky top-0 h-screen overflow-y-auto font-sans">
      <div
        className="mb-3 flex items-center cursor-pointer hover:text-champagne"
        onClick={() => scrollToElement('intro')}
      >
        {iconMap['Get Started']}
        <span className="font-sans font-medium text-sm">{t('Introduction')}</span>
      </div>
      {sections.map((section, index) => {
        const IconComponent = iconMap[section.main_title] || <PiQuestionLight className="h-4 w-4 mr-1" />;
        return (
          <div key={index} className="mb-3">
            <div
              className="flex items-center cursor-pointer hover:text-champagne"
              onClick={() => scrollToElement(`section-${index}`)}
            >
              {IconComponent}
              <span className="font-sans font-medium text-sm">{section.main_title}</span>
            </div>
            <ul className="ml-4 mt-1 space-y-1">
              {section.descriptions.map((desc, descIndex) => (
                <li
                  key={descIndex}
                  className="cursor-pointer hover:text-champagne text-xs"
                  onClick={() => scrollToElement(`description-${index}-${descIndex}`)}
                >
                  {desc.title}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

interface RightContentProps {
  sections: Section[];
  message: string;
}

const RightContent: React.FC<RightContentProps> = ({ sections, message }) => {
  const { t } = useTranslation();
  return (
    <div className="flex-1 p-4 overflow-y-auto font-sans">
      <div id="intro" className="mb-6">
        <h1 className="text-2xl font-sans font-medium mb-3">{t('What is PONS?')}</h1>
        <p className="text-fg-body text-sm whitespace-pre-line">{message}</p>
      </div>
      {/* The id stays on a plain wrapper — it is a scroll anchor for the left
          rail, not something the card itself needs to carry. */}
      {sections.map((section, index) => (
        <div key={index} id={`section-${index}`} className="mb-6">
          <GlassCard
            padding="lg"
            title={
              <span className="flex items-center gap-2">
                {section.main_title}
                {section.status && (
                  <Badge color={section.status === 'Coming soon' ? 'yellow' : 'green'}>
                    {t(section.status)}
                  </Badge>
                )}
              </span>
            }
          >
            {section.descriptions.map((desc, descIndex) => (
              <div key={descIndex} id={`description-${index}-${descIndex}`} className="mb-4">
                <h3 className="text-lg font-sans font-medium mb-2">{desc.title}</h3>
                <ContentRenderer contentItems={desc.content} />
              </div>
            ))}
          </GlassCard>
        </div>
      ))}
    </div>
  );
};

interface ContentRendererProps {
  contentItems: ContentItem[];
}

const ContentRenderer: React.FC<ContentRendererProps> = ({ contentItems }) => {
  const { t } = useTranslation();

  // The manual is backend prose, so the markup this builds out of it goes
  // through the app's sanitizer before reaching dangerouslySetInnerHTML.
  const formatText = (text: string) =>
    sanitizeHTML(
      text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/\n/g, '<br />')
    );

  const renderContent = (items: ContentItem[]) => {
    const renderedContent = [];
    let subInfoItems: ContentItem[] = [];

    items.forEach((item, index) => {
      if (item.type === 'MainInfo') {
        if (subInfoItems.length > 0) {
          renderedContent.push(renderSubInfoGroup(subInfoItems));
          subInfoItems = [];
        }
        renderedContent.push(
          <div key={`maininfo-${index}`} className="mb-4">
            {item.header && <h4 className="text-base font-sans font-medium mb-2">{item.header}</h4>}
            <div className="text-fg-body">
              <p
                dangerouslySetInnerHTML={{ __html: formatText(item.text || '') }}
                className="leading-relaxed text-sm"
              />
            </div>
            {item.sub_content && renderSubContent(item.sub_content)}
          </div>
        );
      } else if (item.type === 'SubInfo') {
        subInfoItems.push(item);
      } else {
        if (subInfoItems.length > 0) {
          renderedContent.push(renderSubInfoGroup(subInfoItems));
          subInfoItems = [];
        }
        renderedContent.push(renderContentItem(item, index));
      }
    });

    if (subInfoItems.length > 0) {
      renderedContent.push(renderSubInfoGroup(subInfoItems));
    }

    return renderedContent;
  };

  const renderSubInfoGroup = (subInfoItems: ContentItem[]) => (
    <div key={`subinfo-group-${subInfoItems[0].header || subInfoItems[0].text}`} className="mb-4">
      {subInfoItems.map((item, idx) => (
        <div key={`subinfo-${idx}`} className="mb-3">
          {item.header && <h5 className="text-base font-sans font-medium mb-1">{item.header}</h5>}
          <div className="text-fg-body">
            <p
              dangerouslySetInnerHTML={{ __html: formatText(item.text || '') }}
              className="leading-relaxed text-sm"
            />
          </div>
        </div>
      ))}
    </div>
  );

  const renderContentItem = (item: ContentItem, index: number) => (
    <div
      key={index}
      className={`mb-4 ${
        item.type === 'Intro' || item.type === 'SubInfo'
          ? ''
          : 'p-3'
      }`}
    >
      <div>
        <div className="flex items-center justify-between mb-1">
          {item.header && <h4 className="text-base font-sans font-medium">{item.header}</h4>}
          {item.status && (
            <Badge
              color={item.status === 'Coming soon' ? 'yellow' : 'green'}
              className="ml-2"
            >
              {t(item.status)}
            </Badge>
          )}
        </div>
        <div className="text-fg-body">
          {item.type === 'Info' && (
            <p
              dangerouslySetInnerHTML={{ __html: formatText(item.text || '') }}
              className="leading-relaxed text-sm"
            />
          )}
          {item.type === 'Intro' && (
            <p
              dangerouslySetInnerHTML={{ __html: formatText(item.text || '') }}
              className="leading-relaxed text-sm"
            />
          )}
          {item.type === 'Tips' && (
            <div className="border-l-4 border-champagne pl-4">
              <p
                dangerouslySetInnerHTML={{ __html: formatText(item.text || '') }}
                className="leading-relaxed italic text-sm"
              />
            </div>
          )}
          {item.type === 'Version' && (
            <div className="italic text-sm">
              <p dangerouslySetInnerHTML={{ __html: formatText(item.text || '') }} />
            </div>
          )}
          {item.type === 'Action' && (
            <div className="mt-2">
              <p
                dangerouslySetInnerHTML={{ __html: formatText(item.text || '') }}
                className="leading-relaxed text-sm"
              />
              {item.link && (
                <a
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-champagne hover:text-champagne/80"
                >
                  {t('Learn more')}
                </a>
              )}
            </div>
          )}
          {item.type === 'Feature' && (
            <>
              <p
                dangerouslySetInnerHTML={{ __html: formatText(item.text || '') }}
                className="leading-relaxed text-sm"
              />
              {item.usage && (
                <div className="mt-3">
                  <label className="font-medium">{t('Usage')}</label>
                  <p
                    dangerouslySetInnerHTML={{ __html: formatText(item.usage || '') }}
                    className="leading-relaxed text-sm"
                  />
                </div>
              )}
              {item.sub_content && renderSubContent(item.sub_content)}
            </>
          )}
          {(item.type === 'Service' || item.type === 'Tips') && item.usage && (
            <div className="mt-3">
              <p>
                <strong>{t('Functionality')}: </strong>
                {item.functionality || ''}
              </p>
              <div className="mt-2">
                <label className="font-medium">{t('Usage')}:</label>
                <p
                  dangerouslySetInnerHTML={{ __html: formatText(item.usage || '') }}
                  className="leading-relaxed text-sm"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderSubContent = (subItems: ContentItem[]) =>
    subItems.map((subItem, idx) => (
      <div key={idx} className="ml-3 mt-3">
        {subItem.header && <h5 className="text-base font-sans font-medium mb-1">{subItem.header}</h5>}
        <div className="text-fg-body">
          <p
            dangerouslySetInnerHTML={{ __html: formatText(subItem.text || '') }}
            className="leading-relaxed text-sm"
          />
        </div>
      </div>
    ));
  return <>{renderContent(contentItems)}</>;
};


export default Help;